/**
 * Agency mark and wordmark.
 *
 * ## Read this before shipping to a real deployment
 *
 * The mark below is a **placeholder**, not the LTO seal. It is a plain
 * geometric device in the agency's colours — deliberately not an imitation of
 * the official emblem, which has a specific protected design (a red disc
 * bearing "LTO", ringed by blue segments and red-and-white rays).
 *
 * Reproducing a government seal from description would produce something that
 * looks official and is not, which is worse than an obvious placeholder. Drop
 * the authentic asset in at `public/lto-seal.svg` and set `USE_OFFICIAL_ASSET`
 * to true; the layout already reserves the right space for it.
 */

const USE_OFFICIAL_ASSET = false;
const OFFICIAL_ASSET_PATH = '/lto-seal.svg';

export function AgencyMark({ size = 28 }: { size?: number }) {
  if (USE_OFFICIAL_ASSET) {
    return (
      <img
        src={OFFICIAL_ASSET_PATH}
        alt="Land Transportation Office"
        width={size}
        height={size}
        style={{ display: 'block' }}
      />
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role="img"
      aria-label="Land Transportation Office (placeholder mark)"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="15" fill="var(--color-brand-blue)" />
      {/* Four spokes — a road/wheel motif, generic rather than the official
          ray pattern. */}
      <g stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" opacity="0.9">
        <line x1="16" y1="3.5" x2="16" y2="8" />
        <line x1="16" y1="24" x2="16" y2="28.5" />
        <line x1="3.5" y1="16" x2="8" y2="16" />
        <line x1="24" y1="16" x2="28.5" y2="16" />
      </g>
      <circle cx="16" cy="16" r="8" fill="var(--color-brand-red)" />
      <text
        x="16"
        y="16"
        textAnchor="middle"
        dominantBaseline="central"
        fill="#ffffff"
        fontSize="7.5"
        fontWeight="700"
        fontFamily="var(--font-sans)"
        letterSpacing="0.2"
      >
        LTO
      </text>
    </svg>
  );
}

/**
 * Mark plus name. `subtitle` carries the system name, so the agency reads as
 * the owner and the system as the thing it owns — rather than the two
 * competing for the same line.
 */
export function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <AgencyMark size={compact ? 24 : 30} />
      <span className="leading-tight">
        <span
          className="block text-sm font-semibold tracking-tight"
          style={{ color: 'var(--color-ink)' }}
        >
          Land Transportation Office
        </span>
        <span
          className="block text-xs"
          style={{ color: 'var(--color-ink-subtle)' }}
        >
          Confirmation System
        </span>
      </span>
    </span>
  );
}

/**
 * The tricolour rule used beneath the header.
 *
 * Three flat bands, no gradient — a small nod to the flag that reads as an
 * official mast-head rather than as decoration.
 */
export function TricolourRule() {
  return (
    <div aria-hidden="true" className="flex h-[3px] w-full">
      <span className="flex-1" style={{ background: 'var(--color-brand-blue)' }} />
      <span className="flex-1" style={{ background: 'var(--color-brand-red)' }} />
      <span className="flex-1" style={{ background: 'var(--color-brand-yellow)' }} />
    </div>
  );
}
